import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import { getUiPersonStatus } from './headcountStatus';
import { personMatchesAlphaRange, AlphaRange, getLastName } from './alphaRangeHelpers';

// ── Counts ──

export interface HeadcountCounts {
  total: number;
  accounted: number;
  notAccounted: number;
  notChecked: number;
}

export const getHeadcountCounts = (
  people: IncidentRosterPerson[],
): HeadcountCounts => {
  const counts: HeadcountCounts = { total: 0, accounted: 0, notAccounted: 0, notChecked: 0 };
  people.forEach((p) => {
    counts.total++;
    const s = getUiPersonStatus(p);
    if (s === 'accounted') counts.accounted++;
    else if (s === 'notAccounted') counts.notAccounted++;
    else counts.notChecked++;
  });
  return counts;
};

// ── CTA labels ──

export type ViewAction = 'Start' | 'Continue' | 'Review' | 'View';

export const getViewActionLabel = (counts: HeadcountCounts): ViewAction => {
  if (counts.notAccounted > 0) return 'Review';
  if (counts.accounted > 0 && counts.notChecked > 0) return 'Continue';
  if (counts.notChecked === counts.total) return 'Start';
  if (counts.notChecked === 0) return 'View';
  return 'Continue';
};

// ── Card summary text ──

export const getCardSummaryText = (counts: HeadcountCounts): string => {
  if (counts.notAccounted > 0) {
    return `${counts.notAccounted} not accounted`;
  }
  if (counts.notChecked === counts.total) {
    return `${counts.notChecked} not checked`;
  }
  if (counts.notChecked === 0 && counts.total > 0) {
    return 'Complete';
  }
  // Mixed
  const parts: string[] = [];
  if (counts.accounted > 0) parts.push(`${counts.accounted} accounted`);
  if (counts.notChecked > 0) parts.push(`${counts.notChecked} not checked`);
  return parts.join(' \u00B7 ');
};

// ── Full Roster state ──

export type FullRosterState = 'notStarted' | 'needsAttention' | 'inProgress' | 'complete' | 'emptyRoster';

export const getFullRosterState = (counts: HeadcountCounts): FullRosterState => {
  if (counts.total === 0) return 'emptyRoster';
  if (counts.notAccounted > 0) return 'needsAttention';
  if (counts.accounted === 0 && counts.notChecked === counts.total) return 'notStarted';
  if (counts.accounted === counts.total && counts.notChecked === 0) return 'complete';
  return 'inProgress';
};

const FULL_ROSTER_DESCRIPTIONS: Record<FullRosterState, string> = {
  notStarted: 'Best option when starting a new headcount.',
  needsAttention: 'Review everyone in the incident roster, including people marked Not Accounted.',
  inProgress: 'Continue reviewing everyone in the incident roster.',
  complete: 'Everyone in the roster has been accounted for.',
  emptyRoster: 'No roster people are currently available.',
};

export const getFullRosterDescription = (state: FullRosterState): string =>
  FULL_ROSTER_DESCRIPTIONS[state];

const FULL_ROSTER_CTA: Record<FullRosterState, string> = {
  notStarted: 'Start Full Roster',
  needsAttention: 'Open Full Roster',
  inProgress: 'Continue Full Roster',
  complete: 'View Full Roster',
  emptyRoster: 'Refresh Roster',
};

export const getFullRosterCtaLabel = (state: FullRosterState): string =>
  FULL_ROSTER_CTA[state];

export const getFullRosterSectionLabel = (state: FullRosterState): string =>
  state === 'notStarted' ? 'Recommended' : 'Full Roster';

export const getFullRosterCountSummary = (counts: HeadcountCounts, state: FullRosterState): string => {
  const n = counts.total;
  const ppl = n === 1 ? 'person' : 'people';
  switch (state) {
    case 'emptyRoster':
      return '0 people';
    case 'notStarted':
      return `${n} ${ppl} \u00B7 ${counts.notChecked} not checked`;
    case 'complete':
      return `${n} ${ppl} \u00B7 complete`;
    case 'needsAttention': {
      const parts = [`${n} ${ppl}`];
      if (counts.accounted > 0) parts.push(`${counts.accounted} accounted`);
      parts.push(`${counts.notAccounted} not accounted`);
      if (counts.notChecked > 0) parts.push(`${counts.notChecked} not checked`);
      return parts.join(' \u00B7 ');
    }
    case 'inProgress': {
      const parts = [`${n} ${ppl}`];
      if (counts.accounted > 0) parts.push(`${counts.accounted} accounted`);
      if (counts.notChecked > 0) parts.push(`${counts.notChecked} not checked`);
      return parts.join(' \u00B7 ');
    }
    default:
      return `${n} ${ppl}`;
  }
};

// ── Friendly labels ──

const PERSON_TYPE_LABELS: Record<string, string> = {
  employee: 'Employees',
  visitor: 'Visitors',
  contractor: 'Contractors',
  tenant: 'Tenants',
  unlisted: 'Manual Add',
};

export const getFriendlyPersonTypeLabel = (raw: string): string => {
  return PERSON_TYPE_LABELS[raw.toLowerCase()] ?? raw;
};

const ROSTER_CONTEXT_LABELS: Record<string, string> = {
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  assignedOffice: 'Assigned Office',
  assignedOfficeNoBadge: 'Assigned Office \u00B7 No Badge Today',
  manualAdd: 'Manual Add',
};

export const getFriendlyGroupLabel = (
  groupType: RosterGroupType,
  rawValue: string,
): string => {
  switch (groupType) {
    case 'personType':
      return getFriendlyPersonTypeLabel(rawValue);
    case 'floor':
      return /^\d+$/.test(rawValue) ? `Floor ${rawValue}` : rawValue;
    case 'practice':
      return rawValue;
    case 'rosterContext':
      return ROSTER_CONTEXT_LABELS[rawValue] ?? rawValue;
    default:
      return rawValue;
  }
};

// ── Group types ──

export type RosterGroupType = 'floor' | 'practice' | 'personType' | 'rosterContext';

export interface RosterViewGroup {
  groupType: RosterGroupType;
  rawValue: string;
  label: string;
  people: IncidentRosterPerson[];
  counts: HeadcountCounts;
  action: ViewAction;
}

export interface RosterViewGroups {
  floors: RosterViewGroup[];
  practices: RosterViewGroup[];
  personTypes: RosterViewGroup[];
  rosterContexts: RosterViewGroup[];
}

// ── Build groups ──

const buildGroupsForDimension = (
  people: IncidentRosterPerson[],
  groupType: RosterGroupType,
  getKey: (p: IncidentRosterPerson) => string | undefined,
  sortAlphabetically = false,
): RosterViewGroup[] => {
  const map = new Map<string, IncidentRosterPerson[]>();
  people.forEach((p) => {
    const key = getKey(p);
    if (key) {
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
  });
  const groups: RosterViewGroup[] = [];
  map.forEach((grpPeople, rawValue) => {
    const counts = getHeadcountCounts(grpPeople);
    groups.push({
      groupType,
      rawValue,
      label: getFriendlyGroupLabel(groupType, rawValue),
      people: grpPeople,
      counts,
      action: getViewActionLabel(counts),
    });
  });
  if (sortAlphabetically) {
    // Sort alphabetically by label
    groups.sort((a, b) => a.label.localeCompare(b.label));
  } else {
    // Sort: groups with notAccounted first, then by size desc, then alphabetical
    groups.sort((a, b) => {
      if (a.counts.notAccounted > 0 && b.counts.notAccounted === 0) return -1;
      if (b.counts.notAccounted > 0 && a.counts.notAccounted === 0) return 1;
      if (b.counts.total !== a.counts.total) return b.counts.total - a.counts.total;
      return a.label.localeCompare(b.label);
    });
  }
  return groups;
};

const getRosterContextKey = (p: IncidentRosterPerson): string | undefined => {
  // A person can appear in multiple contexts; pick the primary one
  if (p.isUnlisted || p.personType === 'unlisted' || p.rosterSource === 'manualAdd' ||
      (p.inclusionReasons && p.inclusionReasons.includes('manualAdd'))) {
    return 'manualAdd';
  }
  if (p.rosterSource === 'lobbyTrack' || (p.inclusionReasons && p.inclusionReasons.includes('lobbyTrack'))) {
    return 'lobbyTrack';
  }
  if (p.isAssignedToIncidentOffice && p.hasIncidentDayBadgeActivity === false) {
    return 'assignedOfficeNoBadge';
  }
  if (p.isAssignedToIncidentOffice || (p.inclusionReasons && p.inclusionReasons.includes('assignedOffice'))) {
    return 'assignedOffice';
  }
  if (p.rosterSource === 'badgeData' || (p.inclusionReasons && p.inclusionReasons.includes('badgeData'))) {
    return 'badgeData';
  }
  return undefined;
};

export const buildRosterViewGroups = (people: IncidentRosterPerson[]): RosterViewGroups => {
  return {
    floors: buildGroupsForDimension(people, 'floor', (p) => p.floor || undefined),
    practices: buildGroupsForDimension(people, 'practice', (p) => p.practice || undefined, true),
    personTypes: buildGroupsForDimension(people, 'personType', (p) => {
      if (p.isUnlisted) return 'unlisted';
      const pt = (p.personType ?? '').toLowerCase().trim();
      return pt && isNaN(Number(pt)) ? pt : undefined;
    }),
    rosterContexts: buildGroupsForDimension(people, 'rosterContext', getRosterContextKey),
  };
};

// ── Alpha range counts ──

export interface AlphaRangeInfo {
  label: string;
  query: string;
  range: AlphaRange;
  count: number;
  counts: HeadcountCounts;
  action: ViewAction;
}

export const getAlphaRangeCounts = (people: IncidentRosterPerson[]): AlphaRangeInfo[] => {
  const ranges: { label: string; query: string; range: AlphaRange }[] = [
    { label: 'A\u2013F', query: 'A-F', range: 'A-F' },
    { label: 'G\u2013L', query: 'G-L', range: 'G-L' },
    { label: 'M\u2013R', query: 'M-R', range: 'M-R' },
    { label: 'S\u2013Z', query: 'S-Z', range: 'S-Z' },
  ];
  return ranges.map((r) => {
    const matched = people.filter((p) => personMatchesAlphaRange(p, r.range));
    const counts = getHeadcountCounts(matched);
    return {
      ...r,
      count: matched.length,
      counts,
      action: getViewActionLabel(counts),
    };
  });
};

// ── Navigation query builders ──

export const buildHeadcountNavQuery = (
  groupType: RosterGroupType,
  rawValue: string,
): string => {
  switch (groupType) {
    case 'floor':
      return `viewId=${encodeURIComponent(`floor:${rawValue}`)}`;
    case 'practice':
      return `viewId=${encodeURIComponent(`practice:${rawValue}`)}`;
    case 'personType':
      return `viewId=${encodeURIComponent(`personType:${rawValue}`)}`;
    case 'rosterContext':
      return `rosterContext=${encodeURIComponent(rawValue)}`;
    default:
      return '';
  }
};

// ── Compact roster state line ──

export const getRosterStateLine = (counts: HeadcountCounts): string => {
  const n = counts.total;
  if (n === 0) return '0 accountability total';
  if (counts.accounted === n && counts.notChecked === 0 && counts.notAccounted === 0) {
    return `${n} accountability total · complete`;
  }
  if (counts.notAccounted > 0) {
    const parts = [`${n} accountability total`, `${counts.notAccounted} need attention`];
    if (counts.notChecked > 0) parts.push(`${counts.notChecked} not checked`);
    return parts.join(' · ');
  }
  if (counts.accounted === 0 && counts.notChecked === n) {
    return `${n} accountability total · headcount not started`;
  }
  // Mixed in-progress
  const parts = [`${n} accountability total`];
  if (counts.accounted > 0) parts.push(`${counts.accounted} accounted`);
  if (counts.notChecked > 0) parts.push(`${counts.notChecked} not checked`);
  return parts.join(' · ');
};

// ── Search matching (for autocomplete results) ──

export const personMatchesRosterSearch = (
  person: IncidentRosterPerson,
  query: string,
): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  const fields: (string | undefined | null)[] = [
    person.displayName,
    person.email,
    person.phone,
    person.practice,
    person.floor,
    person.companyName,
    person.location,
    person.assignedOfficeLocationName,
    person.lastIncidentDayBadgeActivity?.doorName,
    person.lastIncidentDayBadgeActivity?.locationName,
    person.lastIncidentDayBadgeActivity?.readerName,
    person.lastIncidentDayBadgeActivity?.building,
    person.lastIncidentDayBadgeActivity?.floor,
    person.lastIncidentDayBadgeActivity?.area,
    person.personType,
  ];
  return fields.some((f) => f != null && f.toLowerCase().includes(q));
};
